import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {
    Alert,
    Autocomplete,
    Box, Chip,
    Dialog,
    DialogContent,
    Divider, IconButton,
    Stack,
    Tab,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import {TabContext} from "@mui/lab";
import {useTranslation} from "react-i18next";
import {enqueueSnackbar} from "notistack";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import EntityActions from "crqlar-react-common/dist/components/EntityActions";
import Property from "crqlar-react-common/dist/components/Property";
import {useAllServiceCategories} from "crqlar-react-common/dist/utils/hooks/services/useAllServiceCategories";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useAllRootResources} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {useAllResourceCategories} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {ServiceStatus} from "crqlar-react-common/dist/models/service/serviceStatus.model";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import {ServiceCategory} from "crqlar-react-common/src/models/serviceCategory/serviceCategory.model";
import {AgeCategory} from "crqlar-react-common/src/models/ageCategory/ageCategory.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import ButtonImagePicker from "crqlar-react-common/dist/components/ButtonImagePicker";
import ConfirmationSettingsSection from "../newsletterAccount/ConfirmationSettingsSection";
import {RateService} from "crqlar-react-common/dist/services/rate/rate.service";
import RatePreview from "crqlar-react-common/dist/components/RatePreview";
import {useQueryClient} from "react-query";
import {useHasGuestAppModule} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import CAlphaColorPicker from "crqlar-react-common/dist/components/CAlphaColorPicker";
import {getContrastingColor} from "crqlar-react-common/dist/utils/color.util";
import {useListAgendas} from "crqlar-react-common/dist/utils/hooks/agenda/useAgendaData";
import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import {AgendaService} from "crqlar-react-common/dist/services/agenda/agenda.service";
import CloseIcon from "@mui/icons-material/Close";
import {isServiceValid, prepareServiceForSave} from "src/utils/serviceSave.util";

interface Props {
    serviceId: string
    open: boolean
    onClose: () => void
    defaultResourceId?: string
}

const ServiceEditDialog = ({serviceId, open, onClose, defaultResourceId}: Props) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const {hasGuestAppModule} = useHasGuestAppModule()
    const [activeTab, setActiveTab] = useState('basic')
    const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null)

    const categories = useAllServiceCategories()
    const ageCategories = useAllAgeCategories()
    const resources = useAllRootResources()

    const allResourceCategories = useAllResourceCategories()

    const agendasQuery = useListAgendas()
    const agendas = useMemo(() => agendasQuery.data ?? [], [agendasQuery.data])
    const [selectedAgendas, setSelectedAgendas] = useState<AgendaV3[]>([])
    const originalAgendasRef = useRef<AgendaV3[] | null>(null)
    const [agendaChanged, setAgendaChanged] = useState(false)

    const serviceData = useEntity({
        get: async () => serviceId !== 'new' ? ServiceService.getServiceById(serviceId) : (defaultResourceId ? {resourceIds: [defaultResourceId]} : {}),
        create: ServiceService.createService,
        update: async (service) => {
            if (selectedAgendas.length > 0 && originalAgendasRef.current) {
                const modsToUpdate = getModifiedAgendas(selectedAgendas, originalAgendasRef.current, service.id!)
                if (modsToUpdate.length > 0) {
                    await Promise.all(modsToUpdate.map(a => AgendaService.updateAgenda(a)))
                }
            }
            return ServiceService.updateService(service)
        },
        delete: (id) => ServiceService.deleteService({id: [id]}),
        onDeleted: onClose,
        onSaved: () => {
            queryClient.invalidateQueries('allServices', {exact: false})
            agendasQuery.refetch()
            setAgendaChanged(false)
            onClose()
            enqueueSnackbar(t('Changes saved!'), {variant: 'success'})
        },
        isValid: isServiceValid,
        cleanupBeforeSave: prepareServiceForSave
    }, serviceId === 'new' ? undefined : serviceId)

    const service = serviceData.item
    const disabled = !!service.managedExternally
    const setService = serviceData.setItem

    const selectedServiceCategory = categories.find(c => c.id === service.categoryIds?.[0])
    const relevantSectorIds = selectedServiceCategory?.sectorIds ?? service.sectorIds ?? []

    const matchingResourceCategoryIds = new Set(allResourceCategories
        .filter(rc => rc.sectorIds?.some(sid => relevantSectorIds.includes(sid)))
        .map(rc => rc.id as string))

    const sameSectorResourceIds = new Set(resources.filter(resource =>
        resource.sectorIds?.some(sid => relevantSectorIds.includes(sid)) ||
        resource.categoryIds?.some(cid => matchingResourceCategoryIds.has(cid))
    ).map(resource => resource.id))
    const sortedResources = useMemo(
        () => [...resources].sort((left, right) => (left.name ?? '').localeCompare(right.name ?? '')),
        [resources]
    )

    const updateField = (field: keyof Service, value: any) => {
        setService({...service, [field]: value})
    }

    useEffect(() => {
        if (service?.id && agendas.length > 0) {
            const current = agendas.filter(a => a.serviceIds?.includes(service.id!))
            setSelectedAgendas(current)
            if (!originalAgendasRef.current) {
                originalAgendasRef.current = [...current]
            }
        }
    }, [service?.id, agendas])

    const getModifiedAgendas = (selected: AgendaV3[], original: AgendaV3[], serviceId: string): AgendaV3[] => {
        const updated: AgendaV3[] = []
        const selectedIds = new Set(selected.map(a => a.id))
        const originalIds = new Set(original.map(a => a.id))

        original.forEach(agenda => {
            if (!selectedIds.has(agenda.id)) {
                updated.push({...agenda, serviceIds: (agenda.serviceIds ?? []).filter(id => id !== serviceId)})
            }
        })
        selected.forEach(agenda => {
            if (!originalIds.has(agenda.id)) {
                const serviceIds = agenda.serviceIds ?? []
                if (!serviceIds.includes(serviceId)) {
                    updated.push({...agenda, serviceIds: [...serviceIds, serviceId]})
                }
            }
        })
        return updated
    }

    const basicInfoContent = (
        <Stack gap={2}>
            {/* Row 1: Service Category | SKU */}
            <Stack direction={'row'} gap={2} alignItems={'flex-start'}>
                <Box flex={1}>
                    <Typography variant={'body2'} color={'text.secondary'} mb={0.5}>
                        {t('Service Category')}
                    </Typography>
                    <Autocomplete<ServiceCategory>
                        id={'categories'}
                        options={categories}
                        getOptionLabel={(option: ServiceCategory) => option.name || ''}
                        value={categories.find(c => c.id === service.categoryIds?.[0]) ?? null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                                InputProps={{...params.InputProps, sx: {height: 50}}}
                            />
                        )}
                        fullWidth
                        onChange={(_e, value) => {
                            const categoryIds = value ? [value.id as string] : []
                            const firstSectorId = value?.sectorIds?.[0]
                            serviceData.setItem({
                                ...service,
                                categoryIds,
                                sectorIds: firstSectorId ? [firstSectorId] : []
                            })
                        }}
                        disabled={disabled}
                    />
                </Box>
                <Box flex={1}>
                    <Typography variant={'body2'} color={'text.secondary'} mb={0.5}>
                        {t('SKU')}
                    </Typography>
                    <TextField
                        size={'small'}
                        value={service.marketCode ?? ''}
                        onChange={(e) => updateField('marketCode', e.target.value)}
                        fullWidth
                        disabled={disabled}
                        InputProps={{sx: {height: 50}}}
                    />
                </Box>
            </Stack>

            {/* Row 2: Age Categories | Service Duration */}
            <Stack direction={'row'} gap={2} alignItems={'flex-start'}>
                <Box flex={1}>
                    <Typography variant={'body2'} color={'text.secondary'} mb={0.5}>
                        {t('Age categories')}
                    </Typography>
                    <Autocomplete
                        id={'age-categories'}
                        multiple
                        options={ageCategories}
                        getOptionLabel={(option: AgeCategory) => option.name || ''}
                        value={service.ageCategoryIds?.map(id => ageCategories.find(c => c.id === id)).filter(Boolean) as AgeCategory[] || []}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                                InputProps={{...params.InputProps, sx: {height: service.ageCategoryIds?.length ? undefined : 50}}}
                            />
                        )}
                        fullWidth
                        onChange={(_e, value) => updateField('ageCategoryIds', value.map((v: any) => v.id))}
                        disabled={disabled}
                    />
                </Box>
                <Box flex={1}>
                    <Typography variant={'body2'} color={'text.secondary'} mb={0.5}>
                        {t('Service Duration')}
                    </Typography>
                    <TextField
                        size={'small'}
                        value={service.durationMinutes ?? ''}
                        onChange={(e) => updateField('durationMinutes', parseInt(e.target.value))}
                        fullWidth
                        type={'number'}
                        disabled={disabled || service.noShowService}
                        InputProps={{sx: {height: 50}}}
                    />
                </Box>
            </Stack>

            {/* Row 3: Linked Resources (full width) */}
            <Box>
                <Typography variant={'body2'} color={'text.secondary'} mb={0.5}>
                    {t('Linked resources')}
                </Typography>
                <Autocomplete
                    id={'resources'}
                    multiple
                    options={sortedResources}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={service.resourceIds?.map(id => resources.find(c => c.id === id)).filter(Boolean) as any[] || []}
                    renderInput={(params) => <TextField {...params} fullWidth size={'small'}/>}
                    fullWidth
                    onChange={(_e, value) => updateField('resourceIds', value.map((v: any) => v.id))}
                    renderOption={(props, option: any) => {
                        if (!relevantSectorIds.length) {
                            return (
                                <Box component="li" {...props} key={option.id}
                                     style={{display: 'flex', alignItems: 'center'}}>
                                    {option.name}
                                </Box>
                            )
                        }
                        const backgroundColor = sameSectorResourceIds.has(option.id)
                            ? CommonThemeColors.sys.success.light
                            : CommonThemeColors.sys.error.light
                        return (
                            <Box component="li" {...props} key={option.id}
                                 style={{display: 'flex', alignItems: 'center'}}>
                                <Chip label={option.name} style={{backgroundColor, color: 'black'}}/>
                            </Box>
                        )
                    }}
                    disabled={disabled}
                />
            </Box>

            <AdditionalProperties title={t('Content & Translations')}>
                <Stack direction="row" gap={2}>
                    <Stack gap={1} flex={1}>
                        <Property title={t('Name translations')}>
                            <LocalizedTextField
                                id={'name-localized'}
                                size={'small'}
                                value={service.nameLocalized}
                                onChange={(e) => updateField('nameLocalized', e)}
                                fullWidth
                                required
                                disabled={disabled}
                            />
                        </Property>

                        <Divider/>

                        <Property title={t('Short description translations')}>
                            <LocalizedTextField
                                id={'short-description-localized'}
                                size={'small'}
                                value={service.shortDescriptionLocalized}
                                onChange={(e) => updateField('shortDescriptionLocalized', e)}
                                fullWidth
                                required
                                multiline
                                rows={2}
                                disabled={disabled}
                            />
                        </Property>

                        <Divider/>

                        <Property title={t('Description translations')}>
                            <LocalizedTextField
                                id={'description-localized'}
                                size={'small'}
                                value={service.descriptionLocalized}
                                onChange={(e) => updateField('descriptionLocalized', e)}
                                fullWidth
                                required
                                multiline
                                rows={3}
                                disabled={disabled}
                            />
                        </Property>
                    </Stack>

                    <Box sx={{width: 160, flexShrink: 0}}>
                        <Alert severity={'info'} sx={{mb: 1}}>
                            {t('Image size limit info')}
                        </Alert>
                        <ButtonImagePicker
                            id={'image-picker-dialog'}
                            image={service.media}
                            setImage={(image) => updateField('media', image)}
                            width={'100%'}
                            disabled={disabled}
                            onAfterUpload={async (image) => {
                                if (service.id) {
                                    const updated = await ServiceService.updateService({...service, media: image})
                                    serviceData.setItem(updated)
                                }
                            }}
                        />
                    </Box>
                </Stack>
            </AdditionalProperties>
        </Stack>
    )

    const ratesContent = (
        <RatePreview
            entityId={service.id}
            rateIds={service.rateIds}
            noEntityTitle={t('Please save the service to set up rates')}
            noRatesMessage={t('This service has no rates set')}
            onRemove={rateId => {
                updateField('rateIds', service.rateIds?.filter(r => r !== rateId))
            }}
            onAdd={async rate => {
                let rateId = rate.id
                if (!rateId) {
                    const newRate = await RateService.createRate(rate)
                    rateId = newRate.id
                }
                const newRates = service.rateIds?.filter(r => r !== rateId) || []
                newRates.push(rateId!!)
                updateField('rateIds', newRates)
            }}
            defaultRateName={[service.name, 'price'].filter(Boolean).join(' ')}
            disabled={disabled}
        />
    )

    const reservationEmailsContent = (
        <ConfirmationSettingsSection
            confirmationSettings={service?.notificationSettings?.confirmationSettings}
            onSettingChange={(event, value) => {
                updateField('notificationSettings', {
                    ...service?.notificationSettings,
                    confirmationSettings: {
                        ...service?.notificationSettings?.confirmationSettings,
                        [event]: value
                    }
                })
            }}
        />
    )

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <Box sx={{px: 3, pt: 2, pb: 1}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: (service as any)?.color?.background || CommonThemeColors.sys.success.background,
                                flexShrink: 0,
                                cursor: 'pointer',
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                            onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                        />
                        <CAlphaColorPicker
                            color={(service as any)?.color?.background || CommonThemeColors.sys.success.background}
                            anchorEl={colorPickerAnchor}
                            onClose={() => setColorPickerAnchor(null)}
                            onChange={(newColor) => {
                                updateField('color', {
                                    ...(service as any)?.color,
                                    background: newColor,
                                    text: getContrastingColor(newColor)
                                })
                            }}
                        />
                        <TextField
                            variant="standard"
                            value={service.name || ''}
                            onChange={(e) => updateField('name', e.target.value)}
                            placeholder="-"
                            inputProps={{style: {fontSize: '1.25rem', fontWeight: 'bold'}}}
                            disabled={disabled}
                        />
                    </Stack>

                    <Stack direction="row" alignItems="center" gap={2}>
                        <ToggleButtonGroup
                            value={service.status || ServiceStatus.ACTIVE}
                            exclusive
                            onChange={(e, newStatus) => {
                                if (newStatus !== null) updateField('status', newStatus)
                            }}
                            size="small"
                            disabled={disabled}
                            sx={{'& .MuiToggleButtonGroup-grouped.Mui-selected': {borderLeft: '1px solid rgba(0,0,0,0.12) !important'}}}
                        >
                            <ToggleButton
                                value={ServiceStatus.DRAFT}
                                sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: CommonThemeColors.sys.palette.blueGray['50'],
                                        color: CommonThemeColors.sys.palette.blueGray['600'],
                                        '&:hover': {backgroundColor: CommonThemeColors.sys.palette.blueGray['50']}
                                    }
                                }}
                            >
                                {t('Draft')}
                            </ToggleButton>
                            <ToggleButton
                                value={ServiceStatus.ACTIVE}
                                sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: CommonThemeColors.sys.info.background,
                                        color: CommonThemeColors.sys.info.main,
                                        '&:hover': {backgroundColor: CommonThemeColors.sys.info.background}
                                    }
                                }}
                            >
                                {t('Active')}
                            </ToggleButton>
                            {hasGuestAppModule && (
                                <ToggleButton
                                    value={ServiceStatus.ACTIVE_NOT_BOOKABLE}
                                    sx={{
                                        '&.Mui-selected': {
                                            backgroundColor: CommonThemeColors.sys.warning.background,
                                            color: CommonThemeColors.sys.warning.main,
                                            '&:hover': {backgroundColor: CommonThemeColors.sys.warning.background}
                                        }
                                    }}
                                >
                                    {t('Active Not Bookable')}
                                </ToggleButton>
                            )}
                        </ToggleButtonGroup>

                        <EntityActions data={serviceData} forceEnableSave={agendaChanged}/>
                        <IconButton
                            onClick={onClose}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Stack>
                </Stack>

                <TabContext value={activeTab}>
                    <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                        <Tab label={t('Basic info')} value={'basic'}/>
                        <Tab label={t('Rates')} value={'rates'}/>
                        <Tab label={t('Reservation Emails')} value={'emails'}/>
                    </ScrollableTabList>
                </TabContext>
            </Box>

            <DialogContent dividers>
                {activeTab === 'basic' && basicInfoContent}
                {activeTab === 'rates' && ratesContent}
                {activeTab === 'emails' && reservationEmailsContent}
            </DialogContent>
        </Dialog>
    )
}

export default ServiceEditDialog
